import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle,
  BarChart2,
  X as XIcon,
  ChevronRight,
  Hotel,
  Globe,
  Home,
  TrendingUp,
} from "lucide-react";

import { Button } from "../components/ui/button";
import HeaderSearchField from "../components/HeaderSearchField";
import RatingPentagon from "../components/RatingPentagon";
import ServerAPI from "../lib/server";
import Preloader from "../components/ui/Preloader";
import { declOfNum } from "../lib/utils";
import { photoUrl } from "../config";
import searchStore from "../store/searchStore";
import MainFooter from "../components/MainFooter";
import { useNavigate } from "react-router-dom";

const FOR_OWNERS_FEATURES = [
  "Бесплатное размещение объекта",
  "Управление карточкой отеля и номерами",
  "Приём заявок на бронирование",
  "Статистика просмотров и обращений",
  "Ссылки на агрегаторы и соцсети",
  "Мультирейтинг на основе отзывов",
];

const OWNER_CARDS = [
  {
    icon: <Hotel className="w-6 h-6" />,
    title: "Уже работаете с гостями",
    subtitle: "Наведите порядок в бронированиях",
    desc: "Интуитивная PMS‑система для небольших отелей и гостевых домов.",
  },
  {
    icon: <Globe className="w-6 h-6" />,
    title: "Нужны прямые бронирования",
    subtitle: "Получайте гостей без комиссии",
    desc: "Модуль бронирования для вашего сайта.",
  },
  {
    icon: <Home className="w-6 h-6" />,
    title: "Только открываете объект",
    subtitle: "Запустите продажи быстро",
    desc: "Одностраничный лендинг + онлайн-бронирование.",
  },
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: "Хотите увеличить загрузку",
    subtitle: "Получайте дополнительный поток гостей",
    desc: "Размещение в каталоге Гостим.",
  },
];

export default function MainSprav() {
  const navigate = useNavigate();
  const [hotelsCount, setHotelsCount] = useState(0);
  const [hotelsList, setHotelsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    ServerAPI.customPostQuery("/sprav/index", {}).then((res) => {
      searchStore.setHotelsCount(res.hotels_count);
      setHotelsCount(res.hotels_count);
      setHotelsList(res.hotels);
      setLoading(false);
    });
  }, []);

  // Фикс дёрганья: lockUntil 320 мс + порог без -5
  useEffect(() => {
    let lockUntil = 0;
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (!searchStore.isOnFocus) {
        const now = Date.now();
        if (now >= lockUntil) {
          if (currentY > lastScrollY.current + 5) {
            setFiltersVisible(false);
            lockUntil = now + 320;
          } else if (currentY < lastScrollY.current) {
            setFiltersVisible(true);
            lockUntil = now + 320;
          }
        }
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (loading) {
    return <Preloader />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky header */}
      <div
        className="sticky top-0 z-50"
        style={{
          background: "linear-gradient(135deg, #020c1b 0%, #0d2137 100%)",
        }}
      >
        {/* Mobile header — HeaderSearchField */}
        <div className="sm:hidden px-2 py-1 bg-[#2b6cc4]">
          <HeaderSearchField
            searchSubtitle={`${declOfNum(hotelsCount, ["отель", "отеля", "отелей"])}`}
          />
        </div>

        {/* Desktop header — пустой, поиск в hero ниже */}
        <div className="hidden sm:block" />
      </div>

      {/* Hero — desktop only */}
      <section
        className="relative text-white"
        style={{
          background:
            "linear-gradient(135deg, #2b6cc4 0%, #1a5aab 50%, #1246a0 100%)",
          position: "relative",
        }}
      >
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, #14b8a6 0%, transparent 50%), radial-gradient(circle at 80% 20%, #3b82f6 0%, transparent 40%)",
          }}
        />
        {/* Mobile hero text only */}
        <div className="sm:hidden relative px-4 py-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h1 className="text-3xl font-bold mb-4 leading-tight">
              Федеральный гостиничный
              <br />
              <span
                className="text-transparent bg-clip-text"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, #14b8a6, #3b82f6)",
                }}
              >
                справочник Гостим
              </span>
            </h1>
            <p className="text-base text-white/70 max-w-sm mx-auto">
              Найдите идеальное место для отдыха — бесплатно, без лишних
              комиссий
            </p>
          </motion.div>
        </div>

        {/* Desktop hero with search centered */}
        <div className="hidden sm:block relative py-10 lg:py-14">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-medium"
                style={{
                  backgroundColor: "rgba(20,184,166,0.2)",
                  border: "1px solid rgba(20,184,166,0.4)",
                }}
              >
                <BarChart2 className="w-4 h-4 text-teal-400" />
                <span className="text-teal-300">
                  {declOfNum(hotelsCount, ["отель", "отеля", "отелей"])}
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Федеральный гостиничный
                <br />
                <span
                  className="text-transparent bg-clip-text"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, #14b8a6, #3b82f6)",
                  }}
                >
                  справочник Гостим
                </span>
              </h1>
              <div className="max-w-3xl mx-auto mb-6">
                <HeaderSearchField />
              </div>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                Найдите идеальное место для отдыха или разместите свой объект —
                бесплатно, без лишних комиссий
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hotels */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8 flex items-center justify-between"
          >
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">
              Отели
            </h2>
            <a
              href="/russia"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              Все отели →
            </a>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {hotelsList.length > 0 &&
              hotelsList.map((hotel, i) => (
                <motion.button
                  key={i}
                  onClick={() => {
                    if (hotel.url) {
                      navigate(hotel.url, { state: { from: "main" } });
                    }
                  }}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                  className="group block bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow w-full text-left"
                >
                  <div className="relative" style={{ paddingBottom: "100%" }}>
                    {hotel.photos.length > 0 && (
                      <img
                        src={`${photoUrl}/${hotel.photo_url}/hotel/${hotel.photos[0]}.jpg`}
                        alt={hotel.name}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <div className="text-white text-xs font-semibold leading-tight line-clamp-2">
                        {hotel.name}
                      </div>
                      <div className="text-white/70 text-xs mt-0.5">
                        {hotel.city}
                      </div>
                    </div>
                    <div className="absolute top-2 right-2">
                      {hotel.meta_rating > 0 && (
                        <RatingPentagon rating={hotel.meta_rating} size="sm" />
                      )}
                    </div>
                  </div>
                  {hotel?.price > 0 && (
                    <div className="px-3 py-2">
                      <span className="text-xs text-gray-500">от </span>
                      <span className="text-sm font-bold text-gray-900">
                        {(hotel.price || 0).toLocaleString("ru-RU")} ₽
                      </span>
                      <span className="text-xs text-gray-400"> / ночь</span>
                    </div>
                  )}
                </motion.button>
              ))}
          </div>
        </div>
      </section>

      {/* For owners */}
      <section
        className="py-20 text-white"
        style={{
          background:
            "linear-gradient(135deg, #020c1b 0%, #0d2137 35%, #1a3a5c 65%, #0a1628 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Для владельцев отелей
              </h2>
              <p className="text-white/60 text-lg mb-8">
                Разместите свой объект на Гостим.рф и получайте прямые заявки
                без посредников
              </p>
              <a
                href="https://gostim.ru"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  className="px-8 py-4 h-auto text-base font-semibold rounded-full"
                  style={{
                    background: "linear-gradient(to right, #14b8a6, #3b82f6)",
                    color: "#fff",
                  }}
                >
                  Добавить свой объект
                </Button>
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-3"
            >
              {FOR_OWNERS_FEATURES.map((feature, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle
                    className="w-5 h-5 flex-shrink-0"
                    style={{ color: "#14b8a6" }}
                  />
                  <span className="text-white/80">{feature}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Owner scenario cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {OWNER_CARDS.map((card, i) => (
                <div
                  key={i}
                  className="bg-white/5 rounded-2xl p-5 border border-white/10 hover:bg-white/10 hover:border-teal-500/40 transition-all"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-teal-500/15 mb-4 text-teal-400">
                    {card.icon}
                  </div>
                  <h4 className="text-base font-semibold mb-1">{card.title}</h4>
                  <p className="text-white/60 text-sm mb-2">{card.subtitle}</p>
                  <p className="text-white/40 text-xs mb-3">{card.desc}</p>
                  <a
                    href="https://gostim.ru"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-teal-400 hover:text-teal-300 text-sm font-medium transition-colors"
                    style={{ color: "#2dd4bf" }}
                  >
                    Подробнее <ChevronRight className="w-4 h-4" />
                  </a>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 text-center">
            {[
              {
                value: hotelsCount,
                label: `${declOfNum(hotelsCount, ["отель", "отеля", "отелей"], false)} в базе`,
              },
              { value: "0 ₽", label: "Комиссия при бронировании" },
              { value: "12", label: "Типов объектов" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div
                  className="text-3xl sm:text-4xl font-bold mb-2"
                  style={{ color: "#14b8a6" }}
                >
                  {stat.value}
                </div>
                <div className="text-gray-500 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <MainFooter />
    </div>
  );
}
